/**
 * Owner: Masa Bayu
 * File: document-ir.ts
 * Date:Time: 2026-09-18 15:50
 * Description: Canonical Document IR — intermediate only, never a publication format.
 */

import {
  isQXDocumentSourceType,
  QX_DOCUMENT_IR_MODEL_VERSION,
  type QXDocumentSourceType,
} from './codes';
import { assertNoForbiddenKeys, assertSemver, assertValidId, isRecord } from './guards';
import { createQXDocumentId } from './id';
import {
  createQXDocumentAssetRef,
  createQXDocumentBlock,
  createQXDocumentLanguage,
  createQXDocumentLink,
  createQXDocumentMetadata,
  createQXDocumentPageMapEntry,
  createQXDocumentSection,
  createQXDocumentTable,
  createQXDocumentWarning,
  type CreateQXDocumentBlockOptions,
  type QXDocumentAssetRef,
  type QXDocumentBlock,
  type QXDocumentLanguage,
  type QXDocumentLink,
  type QXDocumentMetadata,
  type QXDocumentPageMapEntry,
  type QXDocumentSection,
  type QXDocumentTable,
  type QXDocumentWarning,
} from './parts';
import { createQXDocumentConfidence, type QXDocumentConfidence } from './confidence';
import {
  createQXDocumentProvenance,
  type CreateQXDocumentProvenanceOptions,
  type QXDocumentProvenance,
} from './provenance';
import {
  createQXDocumentLayout,
  type QXDocumentLayout,
} from './layout';

export const QX_DOCUMENT_IR_ID_PREFIX = 'ir' as const;

export const QX_DOCUMENT_IR_ALLOWED_KEYS = [
  'irId',
  'irVersion',
  'sourceType',
  'sourceHash',
  'metadata',
  'language',
  'sections',
  'blocks',
  'assets',
  'tables',
  'links',
  'pageMap',
  'layout',
  'provenance',
  'confidence',
  'warnings',
] as const;

/**
 * Canonical intermediate representation.
 * Document IR ≠ QXW ≠ QXP ≠ QXReader.
 * Optional layout = Eyes geometry (pages/lines/runs) · not chapter semantics.
 */
export interface QXDocumentIR {
  readonly irId: string;
  readonly irVersion: typeof QX_DOCUMENT_IR_MODEL_VERSION;
  readonly sourceType: QXDocumentSourceType;
  readonly sourceHash: string;
  readonly metadata: QXDocumentMetadata;
  readonly language: QXDocumentLanguage;
  readonly sections: readonly QXDocumentSection[];
  readonly blocks: readonly QXDocumentBlock[];
  readonly assets: readonly QXDocumentAssetRef[];
  readonly tables: readonly QXDocumentTable[];
  readonly links: readonly QXDocumentLink[];
  readonly pageMap: readonly QXDocumentPageMapEntry[];
  readonly layout: QXDocumentLayout | null;
  readonly provenance: QXDocumentProvenance;
  readonly confidence: readonly QXDocumentConfidence[];
  readonly warnings: readonly QXDocumentWarning[];
}

export interface CreateQXDocumentIROptions {
  readonly irId?: string;
  readonly sourceType: QXDocumentSourceType;
  readonly sourceHash: string;
  readonly provenance: CreateQXDocumentProvenanceOptions | QXDocumentProvenance;
  readonly metadata?: QXDocumentMetadata;
  readonly language?: QXDocumentLanguage;
  readonly sections?: readonly QXDocumentSection[];
  readonly blocks?: readonly (CreateQXDocumentBlockOptions | QXDocumentBlock)[];
  readonly assets?: readonly QXDocumentAssetRef[];
  readonly tables?: readonly QXDocumentTable[];
  readonly links?: readonly QXDocumentLink[];
  readonly pageMap?: readonly QXDocumentPageMapEntry[];
  readonly layout?: QXDocumentLayout | null | Parameters<typeof createQXDocumentLayout>[0];
  readonly confidence?: readonly QXDocumentConfidence[];
  readonly warnings?: readonly QXDocumentWarning[];
}

export function createQXDocumentIR(options: CreateQXDocumentIROptions): QXDocumentIR {
  if (!isRecord(options)) {
    throw new Error('Document IR must be an object');
  }
  assertNoForbiddenKeys(options, 'Document IR');
  if (!isQXDocumentSourceType(options.sourceType)) {
    throw new Error('Document IR sourceType is unknown');
  }
  if (typeof options.sourceHash !== 'string' || options.sourceHash.trim().length === 0) {
    throw new Error('Document IR sourceHash is required');
  }
  const irId = options.irId ?? createQXDocumentId(QX_DOCUMENT_IR_ID_PREFIX);
  assertValidId(irId, 'Document IR irId');
  assertSemver(QX_DOCUMENT_IR_MODEL_VERSION, 'Document IR irVersion');

  const blocks = (options.blocks ?? []).map((block) => createQXDocumentBlock(block));
  const layout =
    options.layout === undefined || options.layout === null
      ? null
      : createQXDocumentLayout(options.layout);
  const ir: QXDocumentIR = Object.freeze({
    irId,
    irVersion: QX_DOCUMENT_IR_MODEL_VERSION,
    sourceType: options.sourceType,
    sourceHash: options.sourceHash.trim(),
    metadata: options.metadata ?? createQXDocumentMetadata(),
    language: options.language ?? createQXDocumentLanguage(),
    sections: Object.freeze(
      (options.sections ?? []).map((section) => createQXDocumentSection(section)),
    ),
    blocks: Object.freeze(blocks),
    assets: Object.freeze((options.assets ?? []).map((asset) => createQXDocumentAssetRef(asset))),
    tables: Object.freeze((options.tables ?? []).map((table) => createQXDocumentTable(table))),
    links: Object.freeze((options.links ?? []).map((link) => createQXDocumentLink(link))),
    pageMap: Object.freeze(
      (options.pageMap ?? []).map((entry) => createQXDocumentPageMapEntry(entry)),
    ),
    layout,
    provenance: createQXDocumentProvenance(options.provenance),
    confidence: Object.freeze(
      (options.confidence ?? []).map((entry) => createQXDocumentConfidence(entry)),
    ),
    warnings: Object.freeze(
      (options.warnings ?? []).map((warning) => createQXDocumentWarning(warning)),
    ),
  });
  return ir;
}

export function isQXDocumentIR(value: unknown): value is QXDocumentIR {
  if (!isRecord(value)) {
    return false;
  }
  try {
    createQXDocumentIR(value as unknown as CreateQXDocumentIROptions);
    return true;
  } catch {
    return false;
  }
}
